import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './i18n';
import './App.css';
import Header from './components/Header';
import HomePage from './components/HomePage';
import DownloadPage from './components/DownloadPage';
import Footer from './components/Footer';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import HowToPlay from './components/HowToPlay';
import DeleteAccount from './components/DeleteAccount';
import NotFound from './components/NotFound';

function getSupportedLanguage(language) {
  const languageCode = language?.toLowerCase().split('-')[0];

  return SUPPORTED_LANGUAGES.includes(languageCode) ? languageCode : DEFAULT_LANGUAGE;
}

function LanguageUrlSync() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langFromUrl = searchParams.get('lang')?.toLowerCase();
    const isSupportedUrlLang = SUPPORTED_LANGUAGES.includes(langFromUrl);

    if (isSupportedUrlLang && getSupportedLanguage(i18n.resolvedLanguage || i18n.language) !== langFromUrl) {
      i18n.changeLanguage(langFromUrl);
      document.documentElement.lang = langFromUrl;
      return;
    }

    const activeLanguage = getSupportedLanguage(i18n.resolvedLanguage || i18n.language);
    document.documentElement.lang = activeLanguage;

    if (isSupportedUrlLang) {
      return;
    }

    if (activeLanguage !== DEFAULT_LANGUAGE) {
      searchParams.set('lang', activeLanguage);
    } else if (searchParams.has('lang')) {
      searchParams.delete('lang');
    } else {
      return;
    }

    const nextSearch = searchParams.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`;
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;

    if (nextUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [i18n, i18n.language, i18n.resolvedLanguage, location.hash, location.pathname, location.search]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const isHowToPlayPage = location.pathname === '/how';
  const isHomePage = location.pathname === '/';
  const isDownloadPage = /^\/download\/?$/i.test(location.pathname);
  const isStandalonePage = isHowToPlayPage || isDownloadPage;

  return (
    <div className={`App${isHomePage || isDownloadPage ? ' home-page-shell' : ''}`}>
      <LanguageUrlSync />
      {!isStandalonePage && <Header isHomePage={isHomePage} />}
      <main className={isStandalonePage ? "" : `main-content${isHomePage ? ' main-content-home' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/how" element={<HowToPlay />} />
          <Route path="/delete" element={<DeleteAccount />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isStandalonePage && <Footer isHomePage={isHomePage} />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
